import {Permissions} from 'auth/PermissionConstants';
import {OligoAwareFolderItemType, RegistrySupportedType, TagSchema} from 'constants/RegistryConstants';
import {
  IngredientMeasurementUnit,
  MixtureMeasurementUnit,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {DraftState} from 'ui-library/hooks/useDraftState';

import {IngredientGraphQL} from '../../../graphql-fragments/registry/mixtures/__generated/IngredientGraphQL';
import {IngredientTableQuery} from './ingredient-table/__generated/IngredientTableQuery';

type ComponentBioentity = ExtractByTypename<
  IngredientTableQuery['mixture'],
  'MixtureNode'
>['ingredients'][number]['componentBioentity'];

export const DEFAULT_MIXTURE_AMOUNT = {stringValue: '1.0', numericValue: 1};
export const DEFAULT_MIXTURE_UNITS = MixtureMeasurementUnit.L;

export type SupportedType = string | OligoAwareFolderItemType | RegistrySupportedType;

export function isMixtureEditable(permissions: Permissions): boolean {
  return permissions.writable;
}

export const ORDERED_MIXTURE_UNITS_FOR_DISPLAY = [
  MixtureMeasurementUnit.L,
  MixtureMeasurementUnit.mL,
  MixtureMeasurementUnit.uL,
  MixtureMeasurementUnit.nL,
  MixtureMeasurementUnit.kg,
  MixtureMeasurementUnit.g,
  MixtureMeasurementUnit.mg,
  MixtureMeasurementUnit.ug,
  MixtureMeasurementUnit.Units,
];

export const ORDERED_INGREDIENT_UNITS_FOR_DISPLAY = [
  IngredientMeasurementUnit.L,
  IngredientMeasurementUnit.mL,
  IngredientMeasurementUnit.uL,
  IngredientMeasurementUnit.nL,
  IngredientMeasurementUnit.kg,
  IngredientMeasurementUnit.g,
  IngredientMeasurementUnit.mg,
  IngredientMeasurementUnit.ug,
  IngredientMeasurementUnit.Units,
  IngredientMeasurementUnit.Cells,
  IngredientMeasurementUnit.mol,
  IngredientMeasurementUnit.mmol,
  IngredientMeasurementUnit.umol,
];

export interface IngredientREST {
  id: string;
  componentBioentityId: string;
  catalogIdentifier: string | null;
  componentLotValues: {
    text: string | null;
    bioentityId: string | null;
    containerId: string | null;
  };
  amount: string | null;
  units: IngredientMeasurementUnit;
  isInherited: boolean;
  inheritedTargetAmount: string | null;
  notes: string | null;
}

export type MaybeNewIngredient = Omit<IngredientGraphQL, 'id'> & {
  id: string | null;
};

type CreateOrUpdateIngredientParams = Omit<IngredientREST, 'id' | 'isInherited' | 'inheritedTargetAmount'> & {
  id: string | null;
};

export function ingredientToUpdateIngredientParams(
  ingredient: MaybeNewIngredient
): CreateOrUpdateIngredientParams {
  return {
    id: ingredient.id,
    componentBioentityId: ingredient.componentBioentity.id,
    catalogIdentifier: ingredient.catalogIdentifier,
    componentLotValues: {
      text: ingredient.componentLotValues.text,
      bioentityId: ingredient.componentLotValues.bioentity?.id ?? null,
      containerId: ingredient.componentLotValues.container?.id ?? null,
    },
    amount: ingredient.amount,
    units: ingredient.units,
    notes: ingredient.notes,
  };
}

export interface UpdateMixtureParams {
  amount: string;
  units: MixtureMeasurementUnit;
  ingredients: Array<CreateOrUpdateIngredientParams>;
}

export interface MixtureSpecificAttributesREST {
  id: string;
  amount: string;
  units: MixtureMeasurementUnit;
  ingredients: Array<IngredientREST>;
}

export interface UpdateMixtureResponseREST extends MixtureSpecificAttributesREST {}

// Support new ingredient rows with unselected component bioentities
export type EditingIngredient = Omit<MaybeNewIngredient, 'componentBioentity' | 'source'> & {
  componentBioentity: ComponentBioentity | null;
};

export interface QueryingIngredientTableProps {
  tagSchema: TagSchema;
  areIngredientsEditable: boolean;
  mixtureId: string;
  parentLinkFileId: string | null;
  isRegistered: boolean;
}

export type PresentationalIngredientTableProps = QueryingIngredientTableProps &
  (
    | {isLoading: true}
    | {
        isLoading: false;
        draftState: DraftState<IngredientTableData>;
        onAddIngredient: () => void;
        onUpdateIngredient: (ingredientIndex: number, updates: Partial<EditingIngredient>) => void;
        onDeleteIngredient: (ingredientIndex: number) => void;
        onCompleteEdit: () => void;
      }
  );

export interface IngredientTableData {
  mixtureUnits: MixtureMeasurementUnit;
  mixtureAmount: string;
  ingredients: Array<EditingIngredient>;
}
